<script>
    // ===============================
    // IMPORTS
    // ===============================
    import ListFilter from 'lucide-svelte/icons/list-filter';
    import Search from 'lucide-svelte/icons/search';
    import { createEventDispatcher, onMount } from 'svelte';
    import FilterDropdown from './FilterDropdown.svelte';
    import MobileFilterMenu from './MobileFilterMenu.svelte';

    // ===============================
    // EVENT DISPATCHER
    // ===============================
    const dispatch = createEventDispatcher();

    // ===============================
    // COMPONENT PROPS
    // ===============================
    export let headers; // List of table headers
    export let headerOptions; // Options for each header
    export let sortedColumn; // The currently sorted column
    export let sortDirection; // The direction of the current sort
    export let uniqueValues; // Unique value for filterable columns
    export let columnRanges; // The min and max values for each column
    export let filters = {}; // The current filters applied to the table

    // ===============================
    // LOCAL STATE
    // ===============================
    let isMobile = false;
    let initialWidth;
    let showMobileMenuForHeader = null; // Track which filter menu is open on mobile
    let showFilterMenu = {}; // Track the visibility of the filter menu for each column
    let checkboxSearchQueries = {}; // Store search queries for filtering checkboxes
    let filteredValues = {}; // Store filtered values for each header
    let headerClasses = {}; // Store classes for each header

    // ===============================
    // REACTIVE STATEMENTS
    // ===============================
    /**
     * Update filtered values whenever search queries change or filters are applied.
     */
    $: {
        filteredValues = headers.reduce((acc, header) => {
            if (headerOptions[header]?.filterable) {
                const query = checkboxSearchQueries[header] || '';
                acc[header] = uniqueValues[header].filter((value) => value.toString().toLowerCase().includes(query));
            } else {
                acc[header] = uniqueValues[header] || [];
            }
            return acc;
        }, {});
    }

    /**
     * Update header classes based on sorting, filtering, and other options.
     */
    $: {
        headerClasses = headers.reduce((acc, header) => {
            if (header == '') {
                acc[header] = null;
                return acc;
            }

            let classes = [];

            if (headerOptions[header]?.class) {
                classes.push(headerOptions[header]?.class);
            }
            if (headerOptions[header]?.searchable) {
                classes.push('searchable');
            }
            if (headerOptions[header]?.filterable) {
                classes.push('filterable');
            }
            if (headerOptions[header]?.sortable) {
                classes.push('sortable');
            }
            if (sortedColumn === header) {
                classes.push(sortDirection === 'asc' ? 'sorted sorted-asc' : 'sorted sorted-desc');
            }

            const filter = filters[header];
            if (filter?.type === 'selection') {
                const allValuesSelected = filter.selected.size === uniqueValues[header].length;
                if (!allValuesSelected) {
                    classes.push('active');
                }
            } else if (filter) {
                classes.push('active');
            }

            acc[header] = classes.join(' ').trim();
            return acc;
        }, {});
    }

    // ===============================
    // LIFECYCLE METHODS
    // ===============================
    onMount(() => {
        initialWidth = window.innerWidth;
        isMobile = window.innerWidth < 768;
    });

    // onDestroy(() => {
    //     window.removeEventListener('resize', handleResize);
    // });

    // ===============================
    // EVENT HANDLERS
    // ===============================
    /**
     * Show the filter menu when the header's filter button is clicked.
     * @param {string} header - The column header being filtered.
     */
    const handleMobileFilterClick = (header) => {
        showMobileMenuForHeader = showMobileMenuForHeader === header ? null : header;
    };

    /**
     * Check if the window's width has changed.
     */
    const handleResize = () => {
        if (window.innerWidth !== initialWidth) {
            isMobile = window.innerWidth < 768;
            initialWidth = window.innerWidth;
        }
    };

    /**
     * Show the filter menu when the mouse enters the header.
     * @param {string} header - The column header being hovered.
     */
    const onMouseEnter = (header) => {
        if (headerOptions[header]?.filterable && uniqueValues[header]?.length <= 1) {
            return;
        }
        showFilterMenu = { ...showFilterMenu, [header]: true };
    };

    /**
     * Hide the filter menu when the mouse leaves the header.
     * @param {string} header - The column header being hovered.
     */
    const onMouseLeave = (header) => {
        showFilterMenu = { ...showFilterMenu, [header]: false };
    };

    /**
     * Dispatch a sort event for the specified column.
     * @param {string} header - The column header to sort.
     */
    const handleSort = (header) => {
        if (headerOptions[header]?.sortable) {
            dispatch('sort', { header });
        }
    };

    /**
     * Dispatch a filter event when a checkbox is checked or unchecked.
     * @param {string} header - The column header being filtered.
     * @param {string} value - The value being filtered.
     * @param {boolean} checked - Whether the checkbox is checked.
     */
    const handleFilter = (header, value, checked) => {
        if (!filters[header]) {
            filters[header] = { type: 'selection', selected: new Set() };
        }

        if (checked) {
            filters[header].selected.add(value);
        } else {
            filters[header].selected.delete(value);
        }

        if (filters[header].selected.size === 0) {
            filters[header] = { ...filters[header], selected: new Set() };
        }

        dispatch('filter', { header, selectedFilters: Array.from(filters[header].selected) });
    };
</script>

<svelte:window on:resize={handleResize} />

<thead>
    <tr>
        {#each headers as header}
            <th
                class={headerClasses[header]}
                on:mouseenter={() => !isMobile && onMouseEnter(header)}
                on:mouseleave={() => !isMobile && onMouseLeave(header)}
                on:click={() => !isMobile && handleSort(header)}
            >
                {header}

                {#if isMobile}
                    {#if headerOptions[header]?.searchable || headerOptions[header]?.filterable || headerOptions[header]?.sortable}
                        <button class="mobile-menu-button" on:click={() => handleMobileFilterClick(header)}>
                            <ListFilter class="mobile-menu-button-icon" />
                        </button>
                    {/if}
                    {#if header === showMobileMenuForHeader && showMobileMenuForHeader !== null}
                        <MobileFilterMenu
                            {header}
                            {headerOptions}
                            {sortedColumn}
                            {sortDirection}
                            {filters}
                            filterOptions={filteredValues[header]}
                            selectedFilters={filters[header]?.selected || new Set()}
                            {checkboxSearchQueries}
                            minValue={columnRanges[header]?.min}
                            maxValue={columnRanges[header]?.max}
                            on:search={(e) => dispatch('search', e.detail)}
                            on:filter={(e) => handleFilter(header, e.detail.value, e.detail.checked)}
                            on:rangeFilter={(e) => dispatch('rangeFilter', e.detail)}
                            on:sort={(e) => dispatch('sort', e.detail)}
                            on:close={() => {
                                showMobileMenuForHeader = null;
                            }}
                        />
                    {/if}
                {:else if filters[header] && filters[header].type == 'search'}
                    <Search />
                {:else if filters[header]?.type === 'selection'}
                    {#if filters[header].selected.size !== uniqueValues[header].length}
                        <ListFilter />
                    {/if}
                {:else if filters[header]}
                    <ListFilter />
                {/if}

                {#if headerOptions[header]?.searchable || headerOptions[header]?.filterable || headerOptions[header]?.sortable}
                    {#if showFilterMenu[header]}
                        <FilterDropdown
                            {header}
                            {headerOptions}
                            {sortedColumn}
                            {sortDirection}
                            {filters}
                            filterOptions={filteredValues[header]}
                            selectedFilters={filters[header]?.selected || new Set()}
                            {checkboxSearchQueries}
                            minValue={columnRanges[header]?.min}
                            maxValue={columnRanges[header]?.max}
                            on:search={(e) => dispatch('search', e.detail)}
                            on:filter={(e) => handleFilter(header, e.detail.value, e.detail.checked)}
                            on:rangeFilter={(e) => dispatch('rangeFilter', e.detail)}
                            on:sort={(e) => dispatch('sort', e.detail)}
                        />
                    {/if}
                {/if}
            </th>
        {/each}
    </tr>
</thead>

<style>
    th {
        padding: 8px;
        background-color: #f4f4f4;
        line-height: 2;
        text-align: left;
        vertical-align: middle;
    }

    th.right,
    th.numeric {
        text-align: right;
    }

    .filterable,
    .searchable {
        min-width: 200px;
        max-width: 200px;
    }

    .sortable {
        min-width: 80px;
        max-width: 80px;
        cursor: pointer;
    }

    .filterable,
    .searchable,
    .sortable {
        width: fit-content;
        max-width: unset;
        white-space: nowrap;
    }

    .mobile-menu-button {
        padding: 3px;
        border: none;
        border-radius: 4px;
        margin-left: 0.5em;
        background: var(--gray-3);
        color: initial;
        cursor: pointer;
        font-size: 1em;

        @media (prefers-color-scheme: dark) {
            background: var(--cool-80);
            color: var(--color-text-body);
        }
    }

    .mobile-menu-button-icon {
        margin-left: 0;
    }

    .sortable:hover {
        color: #006997;
    }

    @media (prefers-color-scheme: dark) {
        .sortable:hover {
            color: var(--gray-3);
        }
    }

    .sorted-asc::after {
        content: '↑';
    }

    .sorted-desc::after {
        content: '↓';
    }

    .active,
    .sorted {
        max-width: unset;
        color: #006997;
        font-weight: bold;
    }

    .active .mobile-menu-button,
    .sorted .mobile-menu-button {
        background: #006997;
        color: #fff;

        @media (prefers-color-scheme: dark) {
            background-color: var(--cool-80);
        }
    }
</style>
